// src/encounter/medication-route-unit-fields.tsx
// The Route | Unit row of the rail's add-medication form (Flutter medicine_clinical_info_add_widget
// .dart:378-491 `routeUnitView`). Both optional. It owns the two master-list queries, so they only
// run while the form is open: the rail mounts the add dialog permanently with `open={false}`, and a
// query in the dialog body itself would fire on every rail render.
//
// Route follows Flutter's `routeOptions.length != 1` switch: a drug that carries exactly ONE route
// shows it read-only; several → a dropdown of the drug's routes; none → the master list
// (GET /api/Master/MedicationRoute). The current route is always an option, so an edited record
// whose route is not in the list still renders and saves back verbatim (Codex CONFIRMED, PR #177).
// Unit is a dropdown over GET /api/Master/MedicationUnitOfMeasurement, with the current unit kept.
//
// Drawn as Flutter draws it (`dropDownView`, medicine…:997-1049): two borderless DS `InlineField`s,
// "Select route ⌄  route" | "Select unit ⌄  unit", no label above and no box (PROD-3086). The
// select mode reuses the DS listbox; once a value is chosen its chevron becomes the clear cross.
import { useMemo } from 'react';
import { InlineField, type SelectOption } from '@scripsteam/scrips-signal-ds';
import { useMedicationRoutes, useMedicationUnits } from './services/use-medication-masters';
import type { MedRoute } from './lib/to-medication-result';

export interface MedicationRouteUnitFieldsProps {
  /** The picked drug's own routes (catalog `route[]`); absent when the drug carries none. */
  drugRoutes?: MedRoute[];
  route: MedRoute | null;
  onRouteChange: (route: MedRoute | null) => void;
  unit: string;
  onUnitChange: (unit: string) => void;
}

// Two filling fields 24px apart (medicine…:378-491); every root is `min-w-0`, so neither can push
// the row wider than the popover.
const ROW = 'flex min-w-0 items-start gap-6';

export function MedicationRouteUnitFields({ drugRoutes, route, onRouteChange, unit, onUnitChange }: MedicationRouteUnitFieldsProps) {
  const routesQuery = useMedicationRoutes();
  const unitsQuery = useMedicationUnits();

  const routeChoices: MedRoute[] = useMemo(() => {
    const base: MedRoute[] = drugRoutes?.length
      ? drugRoutes
      : (routesQuery.data ?? [])
          .filter((r) => !!r.code)
          .map((r) => ({ code: r.code as string, display: r.display ?? (r.code as string), ...(r.system ? { system: r.system } : {}) }));
    return route && !base.some((r) => r.code === route.code) ? [...base, route] : base;
  }, [drugRoutes, routesQuery.data, route]);

  const routeOptions: SelectOption[] = routeChoices.map((r) => ({ label: r.display || r.code, value: r.code }));

  const unitOptions: SelectOption[] = useMemo(() => {
    const set = new Set(unitsQuery.data ?? []);
    if (unit) set.add(unit);
    return [...set].map((u) => ({ label: u, value: u }));
  }, [unitsQuery.data, unit]);

  const singleRoute = drugRoutes?.length === 1 ? drugRoutes[0] : null;
  const routeError = routeOptions.length === 0 && routesQuery.isError ? 'Couldn’t load routes — retry shortly.' : undefined;
  const unitError = unitsQuery.isError && unitOptions.length === 0 ? 'Couldn’t load units — retry shortly.' : undefined;

  return (
    <div className={ROW} data-field="routeUnit">
      {singleRoute ? (
        // One route on the drug: shown, not chosen (Flutter `routeOptions.length != 1`).
        <InlineField
          aria-label="Route"
          readOnly
          value={singleRoute.display || singleRoute.code}
          suffix="route"
        />
      ) : (
        <InlineField
          mode="select"
          aria-label="Route"
          options={routeOptions}
          value={route?.code || null}
          onChange={(code) => onRouteChange(code ? routeChoices.find((r) => r.code === code) ?? null : null)}
          placeholder="Select route"
          suffix="route"
          error={routeError}
        />
      )}
      <InlineField
        mode="select"
        aria-label="Unit"
        options={unitOptions}
        value={unit || null}
        // The DS select emits null on clear (and on Base UI's deselect); the form's "no unit" is ''.
        onChange={(u) => onUnitChange(u ?? '')}
        placeholder="Select unit"
        suffix="unit"
        error={unitError}
      />
    </div>
  );
}
